import { aiToneSliderConfig } from "@redotech/redo-merchant-marketing-app/common/ai-tone-and-voice/tone-slider-config";
import { RedoRangeSlider } from "@redotech/redo-web/arbiter-components/range-slider/redo-range-slider";
import { Flex } from "@redotech/redo-web/flex";
import { AnimatedSkeletonBlock } from "@redotech/redo-web/skeleton";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import { updateToneRange } from "./helpers";
import { ScrollableStepLayout } from "./scrollable-step-layout";
import {
  getStaggeredContentRevealDelayMs,
  StepMotionItem,
} from "./step-motion";
import type { ConciergeToneStepProps } from "./types";

export const ToneVoiceStep = memo(function ToneVoiceStep({
  animateItems = true,
  stepNumber,
  toneInitPending,
  toneSettings,
  setToneSettings,
}: ConciergeToneStepProps) {
  const title = "Make Concierge sound like your brand";

  return (
    <ScrollableStepLayout
      animateItems={animateItems}
      stepNumber={stepNumber}
      subtitle={
        <>
          We&apos;ve drafted a starting voice for Concierge based on an analysis
          of your store. Adjust the sliders now, and you can fine-tune the
          details later.
        </>
      }
      title={title}
    >
      {toneInitPending ? (
        <StepMotionItem
          animateItems={animateItems}
          delayMs={getStaggeredContentRevealDelayMs(title, 0)}
        >
          <AnimatedSkeletonBlock height="320px" width="100%" />
        </StepMotionItem>
      ) : (
        <Flex dir="column" gap="3xl">
          <Flex dir="column" gap="2xl">
            {aiToneSliderConfig.map((slider, index) => (
              <StepMotionItem
                animateItems={animateItems}
                delayMs={getStaggeredContentRevealDelayMs(title, index)}
                key={slider.key}
              >
                <Flex dir="column" gap="sm">
                  <Text fontSize="sm" fontWeight="medium">
                    {slider.label}
                  </Text>
                  <RedoRangeSlider
                    leftLabel={slider.leftLabel}
                    max={5}
                    onChange={(value) =>
                      setToneSettings((current) =>
                        updateToneRange(current, slider.key, value),
                      )
                    }
                    rightLabel={slider.rightLabel}
                    value={[
                      toneSettings[slider.key].min,
                      toneSettings[slider.key].max,
                    ]}
                  />
                </Flex>
              </StepMotionItem>
            ))}
          </Flex>
          <StepMotionItem
            animateItems={animateItems}
            delayMs={getStaggeredContentRevealDelayMs(
              title,
              aiToneSliderConfig.length,
            )}
          >
            <Text fontSize="sm" textColor="tertiary">
              You can add specific voice instructions later,
              <br />
              including special guidance for different customer moments.
            </Text>
          </StepMotionItem>
        </Flex>
      )}
    </ScrollableStepLayout>
  );
});
